import { ClockIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import type { TipDetailDto, TipsterDetailDto } from '@/api/backoffice-schemas';
import type { TeamMatchDetailDto } from '@/api/backoffice-zod-schemas';
import { cn } from '@/utils/cn';
import { getGameFromBackofficeVideoGame } from '@/utils/gameMappers';
import { formatMatchDatetime } from '@/utils/matches';
import { getTipsterDisplayName } from '@/utils/tipster';
import { GameIcon } from '../GameIcon';
import { AvatarIcon } from '../icons/meta/AvatarIcon';
import { TeamLogo } from '../TeamLogo';

interface HorizontalTipCardProps {
  data: TipDetailDto;
  className?: string;
}

interface TeamInfoProps {
  team?: TeamMatchDetailDto;
  reversed?: boolean;
  tip: TipDetailDto;
}

export const HorizontalTipCard = ({
  data,
  className,
}: HorizontalTipCardProps) => {
  const teamA = data.match?.teamMatches?.[0];
  const teamB = data.match?.teamMatches?.[1];
  const league = data.match?.league;
  const isFinished = data.match?.status === 'finished';
  const game = getGameFromBackofficeVideoGame(data.match?.videoGame?.slug);

  return (
    <Link
      className={cn(
        'rounded-lg bg-card-bg p-4 transition-all hover:bg-dark-gray',
        className
      )}
      href={`/tips/${data.slug}`}
    >
      <div className="flex flex-col items-center gap-4 md:grid md:grid-cols-[1fr_2fr]">
        <div className="flex flex-col items-center gap-6">
          <GameIcon className="size-5" game={game} />
          {league?.imageUrl && (
            <div className="flex items-center justify-center gap-1">
              <Image
                alt="league logo"
                className="block rounded-lg"
                height={32}
                src={league?.imageUrl}
                width={32}
              />
              <span className="font-medium text-xs">{league.name}</span>
            </div>
          )}

          <div className="flex items-center justify-center gap-1">
            <ClockIcon className="size-4 text-blue-gray" />
            <p className="font-medium text-xs leading-4">
              {formatMatchDatetime(data.match?.beginAt)}
            </p>
          </div>
        </div>
        <div
          className={cn(
            'flex w-full flex-col gap-3 rounded-lg bg-gray/5 p-3',
            data.isCorrect && isFinished && 'bg-green-o5',
            !data.isCorrect && isFinished && 'bg-red-o5'
          )}
        >
          <div className="flex w-full flex-col items-center gap-2 md:grid md:grid-cols-[1fr_2rem_1fr]">
            <TeamInfo team={teamA} tip={data} />
            <div className="flex items-center justify-center">
              <div className="flex size-[25px] items-center justify-center rounded-full bg-blue-gray font-semibold text-dark-gray text-xs">
                VS
              </div>
            </div>
            <TeamInfo reversed team={teamB} tip={data} />
          </div>

          {data.description && (
            <p className="line-clamp-3 overflow-hidden text-gray text-sm md:h-14">
              {data.description}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <TipsterInfo tipster={data.tipster} />
          </div>
        </div>
      </div>
    </Link>
  );
};

const TeamInfo = ({ team, reversed, tip }: TeamInfoProps) => {
  const { team: data, score } = team || {};
  const { imageUrl, name } = data || {};
  const { match } = tip;
  const { status } = match || {};

  const isFinished = status === 'finished';
  const matchWinner = match?.winner;
  const isTeamWinner = matchWinner?.externalId === team?.team?.externalId;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 gap-y-2',
        reversed && 'flex-row-reverse'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-3',
          reversed && 'flex-row-reverse'
        )}
      >
        {imageUrl && <TeamLogo name={name} size={24} url={imageUrl} />}
        <span className="font-medium text-sm md:max-w-32">{name || 'TBD'}</span>
      </div>
      <div
        className={cn(
          'flex justify-center rounded-md font-bold text-sm',
          'px-4 py-1',
          'bg-light-blue-o15',
          isTeamWinner && isFinished && 'bg-green-o25',
          !isTeamWinner && isFinished && 'bg-red-o25'
        )}
      >
        {isFinished ? score : '-'}
      </div>
    </div>
  );
};

const TipsterInfo: React.FC<{
  tipster?: TipsterDetailDto;
}> = ({ tipster }) => {
  const name = useMemo(() => {
    return getTipsterDisplayName(tipster);
  }, [tipster]);
  return (
    <div className={cn('flex flex-row items-center gap-1')}>
      {tipster?.profilePhoto ? (
        <Image
          alt="Tipster"
          className="rounded-full object-cover"
          height={24}
          src={tipster.profilePhoto}
          width={24}
        />
      ) : (
        <AvatarIcon
          className="flex items-center justify-center text-blue-gray"
          desc="Tipster avatar icon"
          title="Tipster"
        />
      )}
      <p className="font-normal text-xs uppercase leading-4">{name}</p>
    </div>
  );
};
